import { useState } from 'react';
import { db } from '@/lib/db';
import type { Member, ServiceBranch, ServiceStatus } from '@/lib/db/types';
import { formatServiceTag, SERVICE_BRANCHES, SERVICE_STATUSES } from '@/lib/service-format';
import { Sheet } from '@/components/shared/Sheet';

interface Props {
  member: Member;
  onClose: () => void;
  onSaved: (m: Member) => void;
}

export function EditServiceModal({ member, onClose, onSaved }: Props) {
  const [branch, setBranch] = useState<ServiceBranch | null>(member.service_branch);
  const [rank, setRank] = useState(member.service_rank || '');
  const [status, setStatus] = useState<ServiceStatus | null>(member.service_status);
  const [saving, setSaving] = useState(false);

  const clearAll = async () => {
    setSaving(true);
    try {
      onSaved(await db.members.update({
        service_branch: null,
        service_rank: null,
        service_status: null,
      }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (!branch) {
        await clearAll();
        return;
      }
      onSaved(await db.members.update({
        service_branch: branch,
        service_rank: rank.trim().slice(0, 12) || null,
        service_status: status,
      }));
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const preview = branch ? formatServiceTag(branch, rank, status) : null;

  return (
    <Sheet title="Military Service" onClose={onClose}>
      <div className="px-4 pb-6 pt-2">
        <div className="mb-4 rounded border-l-2 border-gold bg-gold/10 px-3 py-2.5 text-[11.5px] leading-[1.5] text-ink-soft">
          <strong className="text-navy">Optional.</strong>{' '}
          Add your service branch, rank, and status to your profile. Displays as a small text badge to other members.
        </div>

        <div className="mb-[18px]">
          <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">BRANCH</div>
          <div className="grid grid-cols-2 gap-2">
            {SERVICE_BRANCHES.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setBranch(branch === b.id ? null : b.id)}
                className={`flex items-baseline gap-2 rounded-[10px] border-[1.5px] px-3 py-[11px] text-left ${
                  branch === b.id
                    ? 'border-gold bg-navy text-paper'
                    : 'border-line bg-paper text-ink'
                }`}
              >
                <span className={`font-serif text-[11px] font-extrabold tracking-wide ${
                  branch === b.id ? 'text-gold' : 'text-ink-soft'
                }`}>
                  {b.abbr}
                </span>
                <span className="text-xs font-semibold">{b.label}</span>
              </button>
            ))}
          </div>
          {branch && (
            <button
              type="button"
              onClick={() => setBranch(null)}
              className="mt-2 bg-transparent px-0 py-1 text-[11px] text-mute underline"
            >
              Clear branch selection
            </button>
          )}
        </div>

        {branch && (
          <>
            <div className="mb-[18px]">
              <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">RANK (OPTIONAL)</div>
              <input
                value={rank}
                onChange={(e) => setRank(e.target.value)}
                placeholder="e.g., CDR, COL, SGT, LT…"
                maxLength={12}
                className="w-full rounded-[10px] border border-line bg-paper px-3 py-[11px] text-sm text-ink outline-none"
              />
              <div className="mt-1 text-[10.5px] leading-snug text-mute">Use the standard abbreviation for your rank.</div>
            </div>

            <div className="mb-[22px]">
              <div className="mb-2 text-[10px] font-bold tracking-[0.15em] text-mute">STATUS (OPTIONAL)</div>
              <div className="grid grid-cols-2 gap-1.5">
                {SERVICE_STATUSES.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => setStatus(status === s.id ? null : s.id)}
                    className={`rounded-[10px] border-[1.5px] px-3 py-2.5 text-left text-xs font-semibold ${
                      status === s.id
                        ? 'border-gold bg-navy text-paper'
                        : 'border-line bg-paper text-ink'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}

        {preview && (
          <div className="mb-3.5 rounded-xl border border-line bg-cream p-3.5">
            <div className="mb-1.5 text-[10px] font-bold tracking-[0.15em] text-mute">PREVIEW</div>
            <div className="inline-flex items-center gap-1 rounded-md bg-gold/20 px-2.5 py-1 text-xs font-bold tracking-wide text-gold-deep">
              🎖 {preview}
            </div>
          </div>
        )}

        <div className="flex gap-2.5">
          {member.service_branch && (
            <button
              type="button"
              onClick={() => void clearAll()}
              disabled={saving}
              className="flex-1 rounded-[10px] border border-line bg-paper py-3 text-xs font-bold text-bad disabled:opacity-50"
            >
              Remove
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-[10px] border border-line bg-paper py-3 text-[13px] font-bold text-ink"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="flex-[1.5] rounded-[10px] bg-navy py-3 text-[13px] font-bold text-paper disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </Sheet>
  );
}
